import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '../../hooks/useDebounce.js'

// Generic catalogue autocomplete. Drug / Lab / Imaging order forms reuse this
// so we don't duplicate the dropdown behaviour across three components.
//
// Props:
//   searchFn(q)       -> Promise<Array>
//   renderRow(item)   -> ReactNode (optional; default shows {code} {name})
//   rowKey(item)      -> string (defaults to item.code)
//   onPick(item)      -> void
//   placeholder       -> string
//   quickPicks        -> string[] — shortcut chips shown below the search
//                        box when it's focused but empty. Clicking a chip
//                        types that text into the search, triggering the
//                        real catalogue lookup. Useful for surfacing a
//                        curated "commonly prescribed" list without
//                        pretending to know which specific pharmacy row
//                        the doctor wants.
export default function CatalogueAutocomplete({
  searchFn,
  renderRow,
  rowKey = (item) => item.code,
  onPick,
  placeholder,
  queryKey,
  quickPicks,
  // When set, allow the user to commit whatever they typed even if the
  // catalogue has no match. Used by the prescription form so doctors
  // aren't blocked when a pharmacy hasn't stocked the drug they want.
  // The shape passed to onPick is { code: '', name: <typed>, _freeText: true }.
  allowFreeText = false,
  freeTextLabel = 'Use as typed (free-text item)',
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const debounced = useDebounce(q, 200)

  const { data = [], isFetching } = useQuery({
    queryKey: [queryKey, debounced],
    queryFn: () => searchFn(debounced),
    enabled: debounced.trim().length >= 2,
    staleTime: 5 * 60_000,
  })

  function pick(item) {
    onPick(item)
    setQ('')
    setOpen(false)
    setHighlight(0)
    inputRef.current?.focus()
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((i) => Math.min(i + 1, data.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (data[highlight]) pick(data[highlight]) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  const showMenu = open && debounced.trim().length >= 2
  const showQuickPicks = open && q.trim().length === 0 && Array.isArray(quickPicks) && quickPicks.length > 0
  const defaultRender = (item) => (
    <>
      <span className="font-mono text-xs text-wantok-700">{item.code}</span>
      <span className="ml-2">{item.name || item.description}</span>
    </>
  )

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Type 2+ characters…'}
        className="w-full border rounded px-3 py-2 text-sm"
        inputMode="search"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showMenu || showQuickPicks}
      />
      {showQuickPicks && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg p-2 text-sm">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 px-1">
            Commonly prescribed — click to search
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quickPicks.map((name) => (
              <button
                type="button"
                key={name}
                onMouseDown={(e) => {
                  e.preventDefault()
                  setQ(name)
                  setOpen(true)
                  inputRef.current?.focus()
                }}
                className="px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-xs hover:bg-wantok-50 hover:border-wantok-200"
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}
      {showMenu && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto text-sm">
          {isFetching && <div className="p-2 text-slate-400">Searching…</div>}
          {!isFetching && data.length === 0 && !allowFreeText && (
            <div className="p-2 text-slate-400">No matches</div>
          )}
          {data.map((item, i) => (
            <button
              type="button"
              key={rowKey(item)}
              onMouseDown={(e) => { e.preventDefault(); pick(item) }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 ${i === highlight ? 'bg-wantok-50' : 'hover:bg-slate-50'}`}
            >
              {(renderRow || defaultRender)(item)}
            </button>
          ))}
          {!isFetching && allowFreeText && debounced.trim().length >= 2 && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                pick({ code: '', name: debounced.trim(), _freeText: true })
              }}
              className={`block w-full text-left px-3 py-2 border-t ${data.length === 0 ? 'border-transparent' : 'border-slate-100'} bg-amber-50/40 hover:bg-amber-50`}
              title="The catalogue doesn't have this item — commit it as a free-text entry. The pharmacy will see the typed name."
            >
              <span className="text-amber-700">+ </span>
              <span>
                {data.length === 0
                  ? <>{freeTextLabel}: <b>"{debounced.trim()}"</b></>
                  : <>Or use <b>"{debounced.trim()}"</b> as a free-text item</>}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
